import React, { useEffect, useState } from 'react';
import { Box, Divider, Flex, Image, Text, HStack, useTheme, Pressable, Spinner, View } from 'native-base';
import { Platform } from 'react-native';
import InteractionBar from '../common/InteractionBar';
import { getSourceHTML, displayTime, getTopicIDFromURL, checkPrevAndNextPropsAreEqual, checkFlagged, addInlineStyleToMentionBlot, getFriendlyOrDiscourseUsername } from '../../utils/helpers';
import RenderHtml from 'react-native-render-html';
import { DEVICE_WIDTH, AVATAR_SIZE } from '../../Constants';
import { useEnvState } from '../../../context/env';
import { useAuthState } from '../../../context/auth';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faShieldHalved, faFlag } from '@fortawesome/pro-solid-svg-icons';
import Post from '../common/reusables/Post';
import { useDomParser } from '../../hooks/useDomParser';
import RichTextBox from '../common/reusables/RichTextBox';
import { useEditPostDispatch, useEditPostState } from '../../../context/edit';
import UserAvatar from '../common/reusables/UserAvatar';
import { usePostHandler } from '../../hooks/usePostHandler';
import { useReplyDispatch, useReplyState } from '../../../context/reply';
const Reply = /*#__PURE__*/React.memo(function Reply({
  topic,
  reply,
  updateTopicReplies,
  forSingleTopicScreen,
  loadContents,
  likedReplyIDs,
  addLikedReplyID,
  removeLikedReplyID,
  navigation,
  forumKey,
  setPassedReactRef,
  showUserProfile,
  trackForum,
  updateTopicPost
}) {
  const theme = useTheme(),
    {
      colors
    } = theme;
  const envState = useEnvState();
  const authState = useAuthState();
  const {
    getParser,
    normalizeImages,
    displayAtMentions
  } = useDomParser();
  const isHumanCreatedReply = !(reply !== null && reply !== void 0 && reply.action_code);
  const {
    editReplyId,
    loadingReplies,
    loadingNewReply
  } = useEditPostState();
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    if (loadingReplies && editReplyId === reply.id) {
      setShowSpinner(true);
    } else {
      setShowSpinner(false);
    }
  }, [loadingReplies, loadingNewReply, editReplyId, reply.id]);
  const extractedTopicIDFromURL = getTopicIDFromURL(reply === null || reply === void 0 ? void 0 : reply.cooked) ? getTopicIDFromURL(reply === null || reply === void 0 ? void 0 : reply.cooked) : null;
  let sourceHTML = getSourceHTML(reply, colors);
  const avatarTemplate = reply.avatar_template.replace(/\{size\}/, AVATAR_SIZE);
  const isEditing = editReplyId === reply.id;
  const [sharedComment, setSharedComment] = useState(false);
  useEffect(() => {
    (async function () {
      if (forumKey && forumKey.path && forumKey.path.post && forumKey.path.id) {
        if (forumKey.path.post === 'reply') {
          if (reply.id == forumKey.path.id) setSharedComment(true);
        }
      }
    })();
  }, [forumKey]);

  // parse/normalize markup
  const parser = getParser();
  if (parser) {
    sourceHTML = normalizeImages(parser, sourceHTML, envState.layout_width, 'reply');
    sourceHTML = displayAtMentions(parser, sourceHTML);
    sourceHTML = addInlineStyleToMentionBlot(parser, sourceHTML, colors);
  }
  const getFlaggedInfoMessage = () => {
    if (reply) {
      const myAction = checkFlagged(reply); // action object or false
      if (myAction) {
        // should be just 1 as a user can flag each reply once
        if (myAction.id === 3) {
          return 'You flagged this as off-topic';
        } else if (myAction.id === 4) {
          return 'You flagged this as inappropriate';
        } else if (myAction.id === 7) {
          return 'You flagged this for moderation';
        } else if (myAction.id === 8) {
          return 'You flagged this as spam';
        }
      }
    }
    return null;
  };
  const flaggedInfoMessage = getFlaggedInfoMessage();

  // is unique - else is human created
  const boxBackground = sharedComment ? colors.reply.unique.bg : !isHumanCreatedReply ? colors.reply.human.default : colors.reply.human.created;
  const boxBorderColor = sharedComment ? colors.reply.unique.border : colors.reply.border;
  const boxBorderWidth = sharedComment ? 2 : 1;
  const replyUserIsModerator = reply.moderator || reply.group_moderator;
  const posterUser = {
    id: reply.user_id,
    name: reply.display_username,
    username: reply.username,
    moderator: replyUserIsModerator,
    avatar_template: reply.avatar_template,
    accepts_dm: reply.can_send_private_message_to_user
  };
  const isTypeTopic = !isEditing;
  const {
    openModals
  } = useReplyState();
  const replyDispatch = useReplyDispatch();
  let wlcBadge = reply.user_badges.filter(ub => ub.name === 'wlc')[0];
  const [ref] = setPassedReactRef ? setPassedReactRef() : [null];
  const dispatch = useEditPostDispatch();
  const handleEditClick = () => {
    dispatch({
      type: 'HIDE_EDIT_POST_MODAL',
      payload: null
    });
  };
  const handlePress = () => {
    showUserProfile({
      username: `${envState.discourse_api_username_prefix}.${authState.platform_user_id}`,
      avatar_template: reply.avatar_template
    });
  };
  const handleReplyClickClose = () => {
    replyDispatch({
      type: 'HIDE_REPLY_MODAL',
      payload: reply.id
    });
  };
  const userAvatar = reply !== null && reply !== void 0 && reply.yours ? authState === null || authState === void 0 ? void 0 : authState.auth_user_avatar : `${envState === null || envState === void 0 ? void 0 : envState.base_url}${avatarTemplate}`;
  const {
    existingContent,
    handlePostWrapper
  } = usePostHandler({
    topic,
    reply,
    edit: isEditing,
    isTypeTopic: false,
    updateTopicPost,
    updateTopicReplies,
    trackForum
  });
  return /*#__PURE__*/React.createElement("div", {
    id: `reply=${reply.id}`,
    ref: sharedComment ? ref : null
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: 'row',
    justifyContent: 'space-between',
    mb: 1,
    flex: 1
  }, /*#__PURE__*/React.createElement(UserAvatar, {
    onPress: handlePress,
    marginRight: 3,
    userAvatar: userAvatar,
    reply: reply,
    moderator: replyUserIsModerator
  }), isEditing ? /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    ml: 2,
    rounded: 'md'
  }, /*#__PURE__*/React.createElement(RichTextBox, {
    topic: topic,
    existingContent: existingContent,
    isFirstPost: isTypeTopic,
    handlePost: handlePostWrapper,
    setShowModal: handleEditClick,
    id: "edit",
    key: "edit"
  })) : /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    ml: 2,
    my: 1,
    pr: 1,
    pt: 2,
    rounded: 'md',
    bg: boxBackground,
    borderWidth: boxBorderWidth,
    borderColor: boxBorderColor
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: 1,
    px: 4
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: 'row'
  }, /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => showUserProfile(posterUser)
  }, /*#__PURE__*/React.createElement(HStack, {
    alignItems: 'center',
    justifyContent: 'center',
    space: 1
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: 'bold',
    alignSelf: "flex-end"
  }, getFriendlyOrDiscourseUsername(reply, 'friendly'), "."), replyUserIsModerator && /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faShieldHalved,
    color: colors.reply.moderator.icon,
    size: "xl"
  }), !!wlcBadge && /*#__PURE__*/React.createElement(Image, {
    source: {
      uri: wlcBadge.image_url
    },
    alt: wlcBadge.description,
    size: "2xs"
  }))), /*#__PURE__*/React.createElement(Text, {
    ml: 4,
    color: colors.reply.text.created,
    fontSize: '2xs',
    alignSelf: "flex-end"
  }, displayTime(reply.created_at), " ago"), flaggedInfoMessage && /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faFlag,
    color: colors.reply.flagged.icon,
    style: {
      marginLeft: 6
    }
  }))), showSpinner ? /*#__PURE__*/React.createElement(View, {
    justifyContent: 'center',
    alignItems: 'center',
    w: '100%'
  }, /*#__PURE__*/React.createElement(Spinner, null)) : /*#__PURE__*/React.createElement(Flex, {
    direction: 'row',
    pb: !isHumanCreatedReply ? '1' : '0',
    bg: boxBackground
  }, /*#__PURE__*/React.createElement(Text, {
    mx: 4,
    color: colors.reply.text.content,
    mt: 2
  }, /*#__PURE__*/React.createElement(RenderHtml, {
    defaultTextProps: {
      selectable: true
    },
    contentWidth: Platform.OS === 'web' ? null : DEVICE_WIDTH,
    tagsStyles: Post.tagsStyles,
    source: {
      html: sourceHTML
    },
    renderersProps: Post.renderersProps(navigation, extractedTopicIDFromURL, showUserProfile, envState.base_url)
  }))), flaggedInfoMessage && /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.reply.flagged.text,
    italic: true,
    alignSelf: 'flex-end',
    mr: 4
  }, flaggedInfoMessage), isHumanCreatedReply && /*#__PURE__*/React.createElement(Divider, {
    mt: 4
  }), isHumanCreatedReply && /*#__PURE__*/React.createElement(InteractionBar, {
    reply: reply,
    forSingleTopicScreen: forSingleTopicScreen,
    loadContents: loadContents,
    updateTopicReplies: updateTopicReplies,
    likedReplyIDs: likedReplyIDs,
    addLikedReplyID: addLikedReplyID,
    removeLikedReplyID: removeLikedReplyID,
    sharedComment: sharedComment,
    trackForum: trackForum
  }), openModals.includes(reply.id) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    borderRadius: {
      base: 0,
      lg: 8
    }
  }, /*#__PURE__*/React.createElement(HStack, {
    px: 4,
    py: 3,
    borderColor: colors.topic.createButton.border,
    borderTopWidth: {
      base: 1,
      lg: 0
    },
    borderBottomWidth: {
      base: 1,
      lg: 0
    }
  }, /*#__PURE__*/React.createElement(UserAvatar, {
    onPress: handlePress,
    marginRight: 3,
    userAvatar: authState === null || authState === void 0 ? void 0 : authState.auth_user_avatar,
    moderator: replyUserIsModerator
  }), /*#__PURE__*/React.createElement(Box, {
    flex: 1
  }, /*#__PURE__*/React.createElement(RichTextBox, {
    topic: topic,
    topicId: topic === null || topic === void 0 ? void 0 : topic.id,
    handlePost: handlePostWrapper,
    setShowModal: handleReplyClickClose,
    onCloseHandler: handleReplyClickClose,
    id: "newReply",
    key: "newReply"
  }))))))));
});
function replyPropsAreEqual(prevReply, nextReply) {
  const propsToCompare = ['cooked', 'topic_id', 'actions_summary'];
  return checkPrevAndNextPropsAreEqual('reply', prevReply, nextReply, propsToCompare);
}
const MemoizedReply = /*#__PURE__*/React.memo(Reply, replyPropsAreEqual);
export default MemoizedReply;
//# sourceMappingURL=Reply.js.map